import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

import { sendGaPageView } from "../components/CookieConsent";
import { track } from "../utils/track";

const hostOf = (url) => {
  try {
    return url ? new URL(url).hostname || null : null;
  } catch {
    return null;
  }
};

/**
 * One scrubbed GA page view per client-side route change (behaviour-logging
 * TR §11.3). GA's automatic page views are off, because they would send the
 * raw URL — `/app?lat&lng&loc=<address>` — and so would its history-change
 * page views, which are turned off in the GA admin for the same reason.
 *
 * The first render is skipped: the page the visitor landed on is recorded by
 * initAnalyticsIfConsented when GA starts. A change to the query string alone
 * counts only when it is a navigation (push or back/forward); the console
 * rewrites its own query with `replace` to record a selection, and that is not
 * a new page. sendGaPageView does nothing without consent or before GA loads.
 *
 * The first-party `page_view` (behaviour-logging task 6.14) shares these skip
 * rules, so the two cannot disagree about what a page is. It carries the path
 * only — never the query string, which is where the address lives — and,
 * unlike the GA hit, is sent for the first render too: only GA's start-up hit
 * covers the landing page. Its referrer is the external one on landing and
 * none for an in-app navigation. track() has no GA sink for `page_view`, so GA
 * still gets exactly one page view per navigation, from sendGaPageView.
 */
const useGaPageViews = () => {
  const { pathname, search } = useLocation();
  const navigationType = useNavigationType();
  const last = useRef(null);

  useEffect(() => {
    const previous = last.current;
    last.current = { pathname, search };
    if (!previous) {
      track("page_view", {
        path: pathname.slice(0, 500),
        referrer_domain: hostOf(document.referrer),
      });
      return;
    }
    if (previous.pathname === pathname) {
      if (previous.search === search || navigationType === "REPLACE") return;
    }
    sendGaPageView(`${pathname}${search}`);
    track("page_view", { path: pathname.slice(0, 500), referrer_domain: null });
  }, [pathname, search, navigationType]);
};

export default useGaPageViews;
